// A job's pay in the creator's own currency (docs/app-concept.md §16).
//
// The team prices a job in its campaign's currency; a creator sees the fee and
// the tier bonuses in theirs, and reserving fixes exactly what they saw. The
// rates are the team's, as units of each currency per euro. A published job
// converts at the rates it was published with, kept as a snapshot of cross
// rates from its own currency; when a rate drifts more than 5% from a job's
// snapshot, the job follows it for the currencies nobody has reserved it in
// yet, so creators of one currency on one job are always paid alike.
//
// A converted amount is rounded to what people read as a round number: whole
// dollars, euros and pounds, 100 or 500 Ft, 1 or 5 zł. Rounding is to the
// nearest step, so a fee never loses more than half a step, and a bonus that
// pays something never rounds to nothing. Pure; the admin app uses it too.

import {DISPLAY_CURRENCIES} from './money.ts';
import type {BonusTierAmount} from '../shared/creator-api.ts';

/** The currencies a creator can see and be paid in; a rate exists for each. */
export const FX_CURRENCIES: readonly string[] = DISPLAY_CURRENCIES;

/** Units of each currency per euro. */
export type PerEur = Readonly<Record<string, number>>;

/** A move larger than this, against a job's snapshot, is one its unreserved currencies follow. */
export const RATE_MOVE_THRESHOLD = 0.05;

/**
 * Steps in major units: the small one below `from`, the large one from it on.
 * Each is worth roughly a euro or less, so rounding never shows in the pay.
 */
const STEPS: Readonly<Record<string, {small: number; large: number; from: number}>> = Object.freeze({
  USD: {small: 1, large: 1, from: 0},
  EUR: {small: 1, large: 1, from: 0},
  GBP: {small: 1, large: 1, from: 0},
  HUF: {small: 100, large: 500, from: 20_000},
  PLN: {small: 1, large: 5, from: 500},
  CZK: {small: 10, large: 50, from: 5_000},
  RON: {small: 1, large: 5, from: 500},
  SEK: {small: 1, large: 10, from: 1_000},
  DKK: {small: 1, large: 5, from: 1_000},
  AUD: {small: 1, large: 5, from: 1_000},
  BRL: {small: 5, large: 10, from: 1_000},
  CAD: {small: 1, large: 5, from: 1_000},
  CHF: {small: 1, large: 5, from: 1_000},
  ILS: {small: 5, large: 10, from: 1_000},
  ISK: {small: 100, large: 500, from: 20_000},
  JPY: {small: 100, large: 500, from: 20_000},
  KRW: {small: 1_000, large: 5_000, from: 200_000},
  MXN: {small: 10, large: 50, from: 5_000},
  NOK: {small: 10, large: 50, from: 5_000},
  NZD: {small: 1, large: 5, from: 1_000},
  RSD: {small: 100, large: 500, from: 20_000},
  SGD: {small: 1, large: 5, from: 1_000},
  TRY: {small: 50, large: 100, from: 10_000},
  UAH: {small: 50, large: 100, from: 10_000},
  ZAR: {small: 10, large: 50, from: 5_000},
});

export function isFxCurrency(value: unknown): value is string {
  return typeof value === 'string' && FX_CURRENCIES.includes(value);
}

/** The rounding step for an amount, in minor units. */
export function roundingStep(cents: number, currency: string): number {
  const step = STEPS[currency] ?? {small: 1, large: 1, from: 0};
  return (Math.abs(cents) >= step.from * 100 ? step.large : step.small) * 100;
}

/** An amount made round in its currency: to the nearest step, never below one step when positive. */
export function roundAmount(cents: number, currency: string): number {
  if (!Number.isFinite(cents) || cents <= 0) return 0;
  const step = roundingStep(cents, currency);
  return Math.max(step, Math.round(cents / step) * step);
}

/** The same human steps as roundAmount, but a suggested fee always favours the creator. */
export function roundAmountUp(cents: number, currency: string): number {
  if (!Number.isFinite(cents) || cents <= 0) return 0;
  const step = roundingStep(cents, currency);
  return Math.max(step, Math.ceil(cents / step) * step);
}

/** Units of `to` per unit of `from` at these rates; null when either rate is missing. */
export function crossRate(perEur: PerEur, from: string, to: string): number | null {
  if (from === to) return 1;
  const source = perEur[from];
  const target = perEur[to];
  return source && target && source > 0 && target > 0 ? target / source : null;
}

/** The rates a published job converts at: units of each currency per unit of `base`. */
export interface FxSnapshot {
  base: string;
  rates: Record<string, number>;
  /** When these rates were fixed for the job. */
  at: string;
}

export function snapshotFor(perEur: PerEur, base: string, at: string): FxSnapshot {
  const rates: Record<string, number> = {};
  for (const currency of FX_CURRENCIES) {
    const rate = crossRate(perEur, base, currency);
    if (rate) rates[currency] = rate;
  }
  return {base, rates, at};
}

/** A stored snapshot, or null when there is none or it does not read as one. */
export function parseSnapshot(stored: string | null | undefined): FxSnapshot | null {
  if (!stored) return null;
  try {
    const value = JSON.parse(stored) as Partial<FxSnapshot>;
    if (typeof value?.base !== 'string' || !value.rates || typeof value.rates !== 'object') return null;
    const rates: Record<string, number> = {};
    for (const [currency, rate] of Object.entries(value.rates)) {
      if (typeof rate === 'number' && Number.isFinite(rate) && rate > 0) rates[currency] = rate;
    }
    return {base: value.base, rates, at: typeof value.at === 'string' ? value.at : ''};
  } catch {
    return null;
  }
}

/**
 * The rate a job converts at for one creator currency: its snapshot's while
 * the snapshot is for the job's currency, otherwise today's (a job that was
 * never snapshotted is one whose rates have not moved since it was published).
 */
export function jobRate(snapshot: FxSnapshot | null, base: string, target: string, live: number | null): number | null {
  if (base === target) return 1;
  if (snapshot && snapshot.base === base && snapshot.rates[target]) return snapshot.rates[target]!;
  return live && live > 0 ? live : null;
}

/**
 * The 5% rule: the snapshot with every currency whose rate today is more than
 * `threshold` away from it moved to today's rate, except the currencies
 * someone already reserved the job in. Null when nothing moves.
 */
export function followRates(
  snapshot: FxSnapshot,
  perEur: PerEur,
  reserved: ReadonlySet<string>,
  at: string,
  threshold = RATE_MOVE_THRESHOLD,
): FxSnapshot | null {
  const rates = {...snapshot.rates};
  let moved = false;
  for (const currency of FX_CURRENCIES) {
    if (currency === snapshot.base || reserved.has(currency)) continue;
    const today = crossRate(perEur, snapshot.base, currency);
    if (!today) continue;
    const kept = rates[currency];
    // A hair of tolerance, so a move of exactly 5% reads as 5% despite float division.
    if (kept && Math.abs(today / kept - 1) <= threshold + 1e-9) continue;
    rates[currency] = today;
    moved = true;
  }
  return moved ? {base: snapshot.base, rates, at} : null;
}

/** What a job pays, in one currency. */
export interface JobPrice {
  amount_cents: number;
  currency: string;
  bonus_rules: BonusTierAmount[];
}

/** A price converted for one creator, with what it came from. */
export interface PriceQuote extends JobPrice {
  /** Units of `currency` per unit of the source currency; 1 when nothing was converted. */
  rate: number;
  source: JobPrice;
}

/**
 * The price in the creator's currency, rounded (to the nearest step unless
 * `round` says otherwise). Without a rate (an unknown currency, a missing
 * rate) it stays in the job's own currency, which is still the truth, just not
 * converted.
 */
export function quotePrice(
  price: JobPrice, target: string, rate: number | null, round: (cents: number, currency: string) => number = roundAmount,
): PriceQuote {
  if (target === price.currency || !isFxCurrency(target) || !rate || !Number.isFinite(rate) || rate <= 0) {
    return {...price, rate: 1, source: price};
  }
  return {
    amount_cents: round(price.amount_cents * rate, target),
    currency: target,
    bonus_rules: price.bonus_rules.map((rule) => ({tier: rule.tier, amount_cents: round(rule.amount_cents * rate, target)})),
    rate,
    source: price,
  };
}
